import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { getFranchiseSuccess } from "./scheduler";

const cbaBaseAzureUrl = process.env.REACT_APP_BACKEND_URL;

const BOOKING_TIMEOUT_MS = 90000;

// State
const initialState = {
  event: null,
  status: "idle", // idle | loading | ready | error
  slots: [],
  slotsStatus: "idle", // idle | loading | ready | error
  selectedSlot: null,
  guestForm: { idType: "plate" },
  vehicleOptions: { years: [], makes: [], models: [] },
  submitting: false,
  confirmation: null,
  slotConflict: false,
  submitError: null, // null | "timeout" | "failed"
  cancelStatus: "idle", // idle | submitting | done | error
  cancelError: null, // null | "invalid_token" | "cutoff_passed" | "failed"
};

// Slice
const slice = createSlice({
  name: "events",
  initialState: initialState,
  reducers: {
    resetEvent: () => initialState,
    eventLoading: (state) => {
      state.status = "loading";
    },
    eventSuccess: (state, action) => {
      state.event = action.payload;
      state.status = "ready";
    },
    eventError: (state) => {
      state.status = "error";
    },
    slotsLoading: (state) => {
      state.slotsStatus = "loading";
    },
    slotsSuccess: (state, action) => {
      state.slots = action.payload;
      state.slotsStatus = "ready";
    },
    slotsError: (state) => {
      state.slotsStatus = "error";
    },
    setSelectedSlot: (state, action) => {
      state.selectedSlot = action.payload;
      state.slotConflict = false;
    },
    patchGuestForm: (state, action) => {
      state.guestForm = { ...state.guestForm, ...action.payload };
    },
    setVehicleOptions: (state, action) => {
      state.vehicleOptions = { ...state.vehicleOptions, ...action.payload };
    },
    setSubmitting: (state, action) => {
      state.submitting = action.payload;
    },
    setConfirmation: (state, action) => {
      state.confirmation = action.payload;
    },
    setSlotConflict: (state, action) => {
      state.slotConflict = action.payload;
    },
    setSubmitError: (state, action) => {
      state.submitError = action.payload;
    },
    cancelSubmitting: (state) => {
      state.cancelStatus = "submitting";
      state.cancelError = null;
    },
    cancelSuccess: (state) => {
      state.cancelStatus = "done";
    },
    cancelFailed: (state, action) => {
      state.cancelStatus = "error";
      state.cancelError = action.payload;
    },
  },
});

export default slice.reducer;

// Actions
export const {
  resetEvent,
  eventLoading,
  eventSuccess,
  eventError,
  slotsLoading,
  slotsSuccess,
  slotsError,
  setSelectedSlot,
  patchGuestForm,
  setVehicleOptions,
  setSubmitting,
  setConfirmation,
  setSlotConflict,
  setSubmitError,
  cancelSubmitting,
  cancelSuccess,
  cancelFailed,
} = slice.actions;

/**
 * Fetch a store's franchise record by store number, which is the only store
 * identifier an event carries. The payload matches the by-name endpoint, so the
 * existing `getFranchiseSuccess` reducer consumes it unchanged.
 *
 * Resolves `false` on failure: the shared Header/Footer lose their shop links,
 * but the booking page still works.
 */
export const getFranchiseByStore = (storeNumber) => async (dispatch) => {
  try {
    const res = await axios.post(
      `${cbaBaseAzureUrl}/getFranchiseDetailsByStore`,
      {
        storeNumber: String(storeNumber),
      }
    );

    if (res.status === 200) {
      dispatch(getFranchiseSuccess({ res }));
      return true;
    }

    throw new Error(res.data?.data || `Unexpected status ${res.status}`);
  } catch (e) {
    console.error(
      `getFranchiseDetailsByStore failed for store ${storeNumber}:`,
      e.message
    );
    return false;
  }
};

/**
 * Resolve the event, then chain the franchise lookup off its `shopId`. Blocking:
 * `pageMode` decides which screen renders, so a failure means the error state.
 */
export const getEventById = (eventId) => async (dispatch) => {
  dispatch(eventLoading());
  try {
    const res = await axios.post(`${cbaBaseAzureUrl}/getNsdEvent`, { eventId });
    const event = res.data.data;
    dispatch(eventSuccess(event));

    if (event.shopId) {
      dispatch(getFranchiseByStore(event.shopId));
    }
    return event;
  } catch (e) {
    dispatch(eventError());
    return null;
  }
};

/**
 * Slot availability. Keyed only on `eventId`, so it runs alongside the event
 * resolve; non-Classic events return an empty list. Also the refetch after a
 * slot conflict, since the server owns the counts.
 */
export const getEventSlots = (eventId) => async (dispatch) => {
  dispatch(slotsLoading());
  try {
    const res = await axios.post(`${cbaBaseAzureUrl}/getNsdEventSlots`, {
      eventId,
    });
    dispatch(slotsSuccess(res.data.data.slots || []));
    return true;
  } catch (e) {
    dispatch(slotsError());
    return false;
  }
};

/*
 * Vehicle dropdowns come from the same endpoints the main scheduler uses. Unlike
 * the scheduler's thunks these don't redirect to the error page on failure — the
 * guest can still book, the dropdown is just empty.
 */
export const getVehicleYears = () => async (dispatch) => {
  try {
    const res = await axios.get(`${cbaBaseAzureUrl}/getYears`);
    dispatch(setVehicleOptions({ years: res.data.data || [] }));
  } catch (e) {
    console.error("getYears failed:", e.message);
  }
};

export const getVehicleMakes = (year) => async (dispatch) => {
  try {
    const res = await axios.get(
      `${cbaBaseAzureUrl}/getMakesByYear?year=${year}`
    );
    dispatch(setVehicleOptions({ makes: res.data.data || [] }));
  } catch (e) {
    console.error("getMakesByYear failed:", e.message);
  }
};

export const getVehicleModels = (year, make) => async (dispatch) => {
  try {
    const res = await axios.get(
      `${cbaBaseAzureUrl}/getModelsByYearAndMake?year=${year}&make=${encodeURIComponent(
        make
      )}`
    );
    dispatch(setVehicleOptions({ models: res.data.data || [] }));
  } catch (e) {
    console.error("getModelsByYearAndMake failed:", e.message);
  }
};

/** Minutes between consecutive slots, used as the appointment length. */
const slotIntervalMinutes = (slots) => {
  if (!slots || slots.length < 2) return null;
  const toMinutes = (label) => {
    const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(String(label).trim());
    if (!m) return null;
    let h = Number(m[1]) % 12;
    if (/pm/i.test(m[3])) h += 12;
    return h * 60 + Number(m[2]);
  };
  const a = toMinutes(slots[0].time);
  const b = toMinutes(slots[1].time);
  if (a === null || b === null || b <= a) return null;
  return b - a;
};

/** Map the form's field keys onto the registration wire contract. */
const toRegistrationPayload = (event, form, slotTime) => {
  const payload = {
    eventId: event.event_id,
    // Audit label for who performed the booking; the guest acts on their own behalf.
    requesterEmail: form.email,
    firstName: form.firstName,
    lastName: form.lastName,
    phone: form.phone,
    email: form.email,
    heardAbout: form.heard,
    referralOrg: form.referral,
  };

  // Custom events have no slots and no vehicle, so those fields are omitted
  // rather than sent empty.
  if (event.eventType !== "classic") return payload;

  Object.assign(payload, {
    slotTime,
    vehicleNote: form.note,
    vehicleIdType: form.idType,
    make: form.make,
    model: form.model,
  });

  // The validator requires a real int, not a numeric string.
  if (form.year) payload.year = Number(form.year);

  if (form.idType === "vin") {
    payload.vin = form.vin;
  } else {
    payload.plate = form.plate;
    payload.plateState = form.plateState;
  }

  return payload;
};

/**
 * Book the guest. There are no slot holds: the server rechecks capacity and
 * answers 409 `slot_unavailable`, which is why a conflict refetches the grid
 * instead of patching it locally.
 *
 * A 201 may carry a `tekmetric_unavailable` warning. The registration is stored
 * either way, so it isn't surfaced to the guest.
 */
export const submitEventRegistration = () => async (dispatch, getState) => {
  const { event, guestForm, selectedSlot, slots } = getState().events;

  dispatch(setSubmitting(true));
  dispatch(setSlotConflict(false));
  dispatch(setSubmitError(null));

  try {
    const res = await axios.post(
      `${cbaBaseAzureUrl}/createNsdRegistration`,
      toRegistrationPayload(event, guestForm, selectedSlot),
      { timeout: BOOKING_TIMEOUT_MS }
    );

    // Flatten the created row into the shape the confirmation screen reads.
    const reg = res.data.data;
    dispatch(
      setConfirmation({
        ...reg.vehicle,
        registrationId: reg.registrationId,
        firstName: reg.firstName,
        slotTime: reg.slotTime,
        slotMinutes: slotIntervalMinutes(slots),
        event,
      })
    );
    dispatch(setSubmitting(false));
    return { ok: true };
  } catch (e) {
    dispatch(setSubmitting(false));

    const error = e.response?.data?.error;

    if (error === "slot_unavailable") {
      dispatch(setSelectedSlot(null));
      dispatch(setSlotConflict(true));
      dispatch(getEventSlots(event.event_id));
      return { ok: false, error };
    }

    // The event was paused, cancelled or passed its cutoff mid-session — re-resolve
    // so the page falls through to its inactive screen.
    if (error === "event_inactive") {
      dispatch(getEventById(event.event_id));
      return { ok: false, error };
    }

    // A timed-out request may still have been recorded, so the message must not
    // invite a blind retry — there are no slot holds to protect against a double
    // booking.
    if (e.code === "ECONNABORTED") {
      dispatch(setSubmitError("timeout"));
      return { ok: false, error: "timeout" };
    }

    dispatch(setSubmitError("failed"));
    console.error("createNsdRegistration failed:", error || e.message);
    return { ok: false, error: error || "unknown" };
  }
};

/**
 * Guest self-cancel from the link in their confirmation email. The token is a
 * signed registration reference, so it's the only thing the endpoint needs.
 *
 * Never called on mount — link scanners prefetch URLs, and cancelling on load
 * would silently drop a guest's booking.
 */
export const cancelRegistrationByToken = (token) => async (dispatch) => {
  if (!token) {
    dispatch(cancelFailed("invalid_token"));
    return false;
  }

  dispatch(cancelSubmitting());
  try {
    await axios.post(`${cbaBaseAzureUrl}/cancelNsdRegistrationByToken`, {
      token,
    });
    dispatch(cancelSuccess());
    return true;
  } catch (e) {
    const error = e.response?.data?.error;
    if (error === "invalid_token" || error === "missing_required") {
      dispatch(cancelFailed("invalid_token"));
    } else if (error === "cancel_cutoff_passed") {
      dispatch(cancelFailed("cutoff_passed"));
    } else {
      console.error("cancelNsdRegistrationByToken failed:", error || e.message);
      dispatch(cancelFailed("failed"));
    }
    return false;
  }
};
